import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/skate/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/skate/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Zap, Trophy, Crosshair } from "lucide-react";
import { WheelSVG } from "@/components/WheelSVG";

const HERO_IMG = "https://images.unsplash.com/photo-1547442991-a51706bf3a3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxza2F0ZWJvYXJkaW5nJTIwdHJpY2slMjBuaWdodHxlbnwwfHx8fDE3Nzk2NjA5NTV8MA&ixlib=rb-4.1.0&q=85";

const inputCls =
  "bg-[#121212] border-zinc-800 rounded-none h-12 text-base font-mono focus:border-[#FACC15] focus:ring-0";

function extractError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong.";
}

export const Landing = () => {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [form, setForm] = useState({ username: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && user) {
      navigate(user.onboarded ? "/skate/dashboard" : "/skate/onboarding", { replace: true });
    }
  }, [user, loading, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const disabled = useMemo(() => {
    if (!form.username.trim() || !form.password) return true;
    if (mode === "register" && !form.email.trim()) return true;
    return false;
  }, [form, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const body =
        mode === "register"
          ? { username: form.username.trim(), email: form.email.trim(), password: form.password }
          : { username: form.username.trim(), password: form.password };
      const { data } = await api.post(path, body);
      setUser(data);
      navigate(data.onboarded ? "/skate/dashboard" : "/skate/onboarding", { replace: true });
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Hero background */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(80%) contrast(1.05) brightness(0.6)",
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/40 via-black/80 to-black" />
      <div className="absolute inset-0 z-0 grid-bg opacity-50" />

      {/* Top bar */}
      <div className="relative z-10 px-5 pt-6 flex items-center justify-between max-w-2xl mx-auto">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-[#FACC15] pulse-dot" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-400">WHEELS / v1.0</span>
        </div>
        <span className="hidden sm:inline font-mono text-[10px] tracking-[0.3em] text-zinc-500">EST. 2026</span>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-md mx-auto px-5 pt-8 pb-12 min-h-[90vh] flex flex-col">
        {/* Logo */}
        <div className="flex justify-center mb-5 fade-up">
          <div
            className="w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center rounded-full"
            style={{
              backgroundColor: "#0a0a0a",
              border: "3px solid #FACC15",
              boxShadow: "0 0 40px rgba(250, 204, 21, 0.35), inset 0 0 30px rgba(250, 204, 21, 0.08)",
            }}
          >
            <div className="w-[85%] h-[85%]"><WheelSVG eraKey="spoked_wheel" /></div>
          </div>
        </div>

        <div className="fade-up text-center" style={{ animationDelay: ".1s" }}>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#FACC15] mb-3">▌ STREET SKATE LEAGUE</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-[0.9] text-white">
            <span className="text-[#FACC15]">WHEELS</span> DECIDE<br />WHO RIPS.
          </h1>
        </div>

        {/* Auth card */}
        <div className="mt-8 fade-up border border-zinc-800 bg-black/70 backdrop-blur-md p-5" style={{ animationDelay: ".2s" }}>
          {/* Tabs */}
          <div className="grid grid-cols-2 gap-px bg-zinc-800 mb-5 border border-zinc-800">
            <button
              type="button"
              data-testid="tab-login"
              onClick={() => { setMode("login"); setError(null); }}
              className={`py-2 font-display text-sm tracking-widest transition-colors ${mode === "login" ? "bg-[#FACC15] text-black" : "bg-black text-zinc-400 hover:text-white"}`}
            >
              LOG IN
            </button>
            <button
              type="button"
              data-testid="tab-register"
              onClick={() => { setMode("register"); setError(null); }}
              className={`py-2 font-display text-sm tracking-widest transition-colors ${mode === "register" ? "bg-[#FACC15] text-black" : "bg-black text-zinc-400 hover:text-white"}`}
            >
              REGISTER
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            <div>
              <label className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase block mb-2">Username</label>
              <Input
                data-testid="username-input"
                autoFocus
                value={form.username}
                onChange={(e) => update("username", e.target.value)}
                placeholder="kickflip_kid"
                className={inputCls}
                autoComplete="username"
              />
            </div>

            {mode === "register" && (
              <div>
                <label className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase block mb-2">Email</label>
                <Input
                  data-testid="email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                  className={inputCls}
                  autoComplete="email"
                />
              </div>
            )}

            <div>
              <label className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase block mb-2">Password</label>
              <Input
                data-testid="password-input"
                type="password"
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                placeholder="••••••••"
                className={inputCls}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              />
            </div>

            {error && (
              <div
                data-testid="auth-error"
                className="p-3 border border-red-500/60 bg-red-500/10 font-mono text-[11px] text-red-200"
              >
                {error}
              </div>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={disabled || submitting}
              className="w-full bg-[#FACC15] hover:bg-[#FDE047] active:scale-[0.99] text-black font-display text-lg tracking-widest h-12 rounded-none border-0 transition-all disabled:opacity-50"
            >
              {submitting ? "…" : (mode === "register" ? "CREATE ACCOUNT" : "ENTER THE LEAGUE")}
              <ArrowRight className="ml-3 w-5 h-5" />
            </Button>
          </form>

          <p className="font-mono text-[10px] text-zinc-500 tracking-widest mt-4 text-center">
            {mode === "register"
              ? "REGISTER → PICK YOUR CITY → CHALLENGE."
              : "LOG IN → JUMP BACK IN."}
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 mt-8 fade-up" style={{ animationDelay: ".3s" }}>
          {[
            { icon: Crosshair, k: "1V1", v: "MATCHES" },
            { icon: Trophy,    k: "ELO", v: "RANKED" },
            { icon: Zap,       k: "REAL", v: "WORLD" },
          ].map(({ icon: I, k, v }, idx) => (
            <div key={idx} className="bg-black p-4">
              <I className="w-4 h-4 text-[#FACC15] mb-1" strokeWidth={2} />
              <div className="font-display text-2xl">{k}</div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-zinc-900 bg-black/80 backdrop-blur-md py-3 overflow-hidden">
        <div className="marquee whitespace-nowrap font-display text-2xl tracking-tight flex gap-12 px-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="flex items-center gap-12">
              <span>NO VIDEOS</span><span className="text-[#FACC15]">●</span>
              <span>NO LIKES</span><span className="text-[#FACC15]">●</span>
              <span>JUST RANK</span><span className="text-[#FACC15]">●</span>
              <span>REAL SESSIONS</span><span className="text-[#FACC15]">●</span>
              <span>BRONZE TO PRO</span><span className="text-[#FACC15]">●</span>
              <span>STREET CRED</span><span className="text-[#FACC15]">●</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Landing;
